import React from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface EditorialMarkdownProps {
  children: string;
  /** Slightly tighter type for metadata blocks (certifications, organizations) */
  size?: 'base' | 'sm';
  className?: string;
}

/**
 * Safe markdown renderer for therapy knowledge content.
 * - Uses react-markdown (no dangerouslySetInnerHTML)
 * - Inherits the editorial Resonow palette (warm stone tones, serif headings)
 * - Generous spacing for long-form reading
 */
const EditorialMarkdown: React.FC<EditorialMarkdownProps> = ({ children, size = 'base', className }) => {
  const text = (size === 'sm')
    ? 'text-[15px] leading-[1.8]'
    : 'text-base md:text-[17px] leading-[1.85]';

  const components: Components = {
    h1: ({ node, ...props }) => (
      <h3 className="font-serif text-xl md:text-2xl text-stone-900 mt-10 mb-4 tracking-tight" {...props} />
    ),
    h2: ({ node, ...props }) => (
      <h3 className="font-serif text-xl md:text-2xl text-stone-900 mt-10 mb-4 tracking-tight" {...props} />
    ),
    h3: ({ node, ...props }) => (
      <h4 className="font-serif text-lg md:text-xl text-stone-900 mt-8 mb-3 tracking-tight" {...props} />
    ),
    h4: ({ node, ...props }) => (
      <h5 className="font-sans text-[11px] uppercase tracking-[0.25em] text-stone-500 mt-7 mb-3" {...props} />
    ),
    h5: ({ node, ...props }) => (
      <h5 className="font-sans text-[11px] uppercase tracking-[0.25em] text-stone-500 mt-7 mb-3" {...props} />
    ),
    h6: ({ node, ...props }) => (
      <h6 className="font-sans text-[11px] uppercase tracking-[0.25em] text-stone-500 mt-7 mb-3" {...props} />
    ),
    p: ({ node, ...props }) => (
      <p className={`text-stone-700 font-sans ${text} mb-5`} {...props} />
    ),
    strong: ({ node, ...props }) => (
      <strong className="font-semibold text-stone-900" {...props} />
    ),
    em: ({ node, ...props }) => (
      <em className="italic text-stone-800" {...props} />
    ),
    ul: ({ node, ...props }) => (
      <ul className={`list-disc pl-6 mb-6 space-y-2 marker:text-[hsl(var(--primary)/0.7)] text-stone-700 font-sans ${text}`} {...props} />
    ),
    ol: ({ node, ...props }) => (
      <ol className={`list-decimal pl-6 mb-6 space-y-2 marker:text-stone-500 marker:font-serif marker:italic text-stone-700 font-sans ${text}`} {...props} />
    ),
    li: ({ node, ...props }) => (
      <li className="pl-1 leading-[1.75]" {...props} />
    ),
    blockquote: ({ node, ...props }) => (
      <blockquote
        className="border-l-2 border-[hsl(var(--primary)/0.4)] pl-5 my-6 font-serif italic text-stone-700 text-[17px] md:text-lg leading-relaxed"
        {...props}
      />
    ),
    a: ({ node, ...props }) => (
      <a
        className="text-amber-700 hover:text-amber-900 underline underline-offset-2 decoration-[hsl(var(--primary)/0.4)]"
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      />
    ),
    hr: () => <hr className="my-10 border-stone-200" />,
    code: ({ node, ...props }) => (
      <code className="font-mono text-[0.92em] bg-stone-100 px-1.5 py-0.5 rounded-sm text-stone-800" {...props} />
    ),
    table: ({ node, ...props }) => (
      <div className="my-6 overflow-x-auto">
        <table className="w-full text-sm font-sans border-collapse" {...props} />
      </div>
    ),
    th: ({ node, ...props }) => (
      <th className="text-left font-semibold text-stone-900 border-b border-stone-300 py-2 pr-4" {...props} />
    ),
    td: ({ node, ...props }) => (
      <td className="border-b border-stone-200 py-2 pr-4 text-stone-700 align-top" {...props} />
    ),
  };

  return (
    <div className={className}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {children}
      </ReactMarkdown>
    </div>
  );
};

export default EditorialMarkdown;
